import { PAGE_SECTIONS } from '../const'
import type { IElement, IPage } from '../types'
import { floatingElementsSet } from './flatChildrens'
import { usesContentRelativePagePositioning } from './positioning'

/** Minimum box size to trust DOM measurement (collapsed / transition frames). */
const MIN_LAYOUT_BOX = 8

/** Pixel geometry relative to the bound container (same rect math as drag-end, without anchor inference). */
export type FloatingLayoutGeometry = {
  top: number
  left: number
  bottom: number
  right: number
  height: number
  width: number
}

export function buildBoundContainerSelector(
  pageIndex: number,
  sectionType: typeof PAGE_SECTIONS.HEADERS | typeof PAGE_SECTIONS.FOOTERS | null
): string {
  if (sectionType === PAGE_SECTIONS.HEADERS) {
    return `.boundContainer-${pageIndex}-section-headers`
  }
  if (sectionType === PAGE_SECTIONS.FOOTERS) {
    return `.boundContainer-${pageIndex}-section-footers`
  }
  return `.boundContainer-${pageIndex}`
}

function escapeForAttributeSelector(id: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(id)
  }
  return id.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
}

/**
 * Measures the movable target's box relative to the page/section bound container.
 * Does not infer preferBottom/preferRight — those come from persisted styles.
 */
export function measureFloatingElementGeometryFromDom(
  elementId: string,
  pageIndex: number,
  sectionType: typeof PAGE_SECTIONS.HEADERS | typeof PAGE_SECTIONS.FOOTERS | null
): FloatingLayoutGeometry | null {
  if (typeof document === 'undefined') return null

  const containerSelector = buildBoundContainerSelector(pageIndex, sectionType)
  const container = document.querySelector(containerSelector) as HTMLElement | null
  if (!container) return null

  const safeId = escapeForAttributeSelector(elementId)
  const target = container.querySelector(`[data-element-id="${safeId}"]`) as HTMLElement | null
  if (!target) return null

  const targetRect = target.getBoundingClientRect()
  const containerRect = container.getBoundingClientRect()
  const height = targetRect.height
  const width = targetRect.width
  if (!Number.isFinite(height) || !Number.isFinite(width) || height < MIN_LAYOUT_BOX || width < MIN_LAYOUT_BOX) {
    return null
  }

  const snappedTop = targetRect.top - containerRect.top + (container.scrollTop || 0)
  const snappedLeft = targetRect.left - containerRect.left + (container.scrollLeft || 0)
  const snappedBottom = containerRect.height - (snappedTop + height)
  const snappedRight = containerRect.width - (snappedLeft + width)

  return {
    top: snappedTop < -1 ? 0 : snappedTop,
    left: snappedLeft < 0 ? 0 : snappedLeft,
    bottom: snappedBottom,
    right: snappedRight < 0 ? 0 : snappedRight,
    height,
    width,
  }
}

/** Draggable ids on this page whose persisted position uses bottom anchoring (`preferBottom: true`). */
export function collectBottomPreferDraggableIdsFromPage(page: IPage): string[] {
  const acc: string[] = []

  const visit = (elements?: IElement[]) => {
    if (!elements?.length) return
    for (const el of elements) {
      if (el?.component?.isDraggable && el.id && el.responsiveStyles?.large?.position?.preferBottom === true) {
        acc.push(el.id)
      }
      if (el.children?.length) {
        visit(el.children)
      }
    }
  }

  visit(page.children)
  visit(page.component?.options?.headers?.children)
  visit(page.component?.options?.footers?.children)

  return acc
}

export function findElementOnSinglePage(page: IPage, elementId: string): IElement | null {
  const walk = (nodes?: IElement[]): IElement | null => {
    if (!nodes?.length) return null
    for (const node of nodes) {
      if (node.id === elementId) return node
      const nested = walk(node.children)
      if (nested) return nested
    }
    return null
  }

  return walk(page.children) || walk(page.component?.options?.headers?.children) || walk(page.component?.options?.footers?.children) || null
}

/** Bound container for a draggable on this page: main content uses `null`. */
export function getFloatingBoundSectionForElementOnPage(
  page: IPage,
  elementId: string
): typeof PAGE_SECTIONS.HEADERS | typeof PAGE_SECTIONS.FOOTERS | null {
  const inSubtree = (nodes?: IElement[]): boolean => {
    if (!nodes?.length) return false
    for (const n of nodes) {
      if (n.id === elementId) return true
      if (inSubtree(n.children)) return true
    }
    return false
  }

  if (inSubtree(page.component?.options?.headers?.children)) {
    return PAGE_SECTIONS.HEADERS
  }
  if (inSubtree(page.component?.options?.footers?.children)) {
    return PAGE_SECTIONS.FOOTERS
  }
  return null
}

/** Larger epsilon for layout resync to avoid subpixel / font reflow churn. */
const BOTTOM_PREFER_RESYNC_EPS = 1.25

type PositionLike = {
  top?: number
  left?: number
  bottom?: number
  right?: number
  preferRight?: boolean
}

function num(v: unknown): number {
  if (typeof v === 'number') return v
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

function readPositiveDim(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v) && v >= MIN_LAYOUT_BOX) return v
  if (typeof v === 'string') {
    const n = parseFloat(v)
    if (Number.isFinite(n) && n >= MIN_LAYOUT_BOX) return n
  }
  return 0
}

/**
 * Recompute top/left/right from stored bottom anchor + dimensions when sibling layout
 * changes the bound container height. Avoids reading the movable target during editor
 * reflow (zero-height / stale frames) which could write a bogus `bottom` and hide the field.
 */
export function computeBottomPreferGeometryFromStoredLayout(
  element: IElement,
  pageIndex: number,
  sectionType: typeof PAGE_SECTIONS.HEADERS | typeof PAGE_SECTIONS.FOOTERS | null
): FloatingLayoutGeometry | null {
  if (typeof document === 'undefined') return null

  const pos = element.responsiveStyles?.large?.position
  if (!pos?.preferBottom || !element.id) return null

  const container = document.querySelector(buildBoundContainerSelector(pageIndex, sectionType)) as HTMLElement | null
  if (!container) return null

  const rect = container.getBoundingClientRect()
  const ch = rect.height
  const cw = rect.width
  if (!Number.isFinite(ch) || ch <= 0 || !Number.isFinite(cw) || cw <= 0) return null

  let height = readPositiveDim(element.responsiveStyles?.large?.dimensions?.height)
  let width = readPositiveDim(element.responsiveStyles?.large?.dimensions?.width)

  if (height < MIN_LAYOUT_BOX || width < MIN_LAYOUT_BOX) {
    const dom = measureFloatingElementGeometryFromDom(element.id, pageIndex, sectionType)
    if (!dom) return null
    if (height < MIN_LAYOUT_BOX) height = dom.height
    if (width < MIN_LAYOUT_BOX) width = dom.width
  }
  if (height < MIN_LAYOUT_BOX || width < MIN_LAYOUT_BOX) return null

  const bottom = num(pos.bottom)
  const preferRight = !!pos.preferRight

  let top = ch - bottom - height
  let left: number
  let right: number

  if (preferRight) {
    right = num(pos.right)
    left = cw - right - width
  } else {
    left = num(pos.left)
    right = cw - left - width
  }

  if (top < -1) top = 0
  if (left < 0) left = 0
  if (right < 0) right = 0

  if (!Number.isFinite(top) || !Number.isFinite(left) || !Number.isFinite(right) || !Number.isFinite(bottom)) {
    return null
  }

  if (top > ch + 200 || left > cw + 200) {
    return null
  }

  return {
    top,
    left,
    bottom,
    right,
    height,
    width,
  }
}

/**
 * Whether persisted position differs from measured geometry enough to write.
 * Only used for elements that already have `preferBottom: true` in the store.
 */
export function bottomPreferLayoutNeedsWrite(stored: PositionLike | undefined, measured: FloatingLayoutGeometry): boolean {
  if (!stored) return true
  if (Math.abs(num(stored.bottom) - measured.bottom) >= BOTTOM_PREFER_RESYNC_EPS) return true
  if (stored.preferRight) {
    if (Math.abs(num(stored.right) - measured.right) >= BOTTOM_PREFER_RESYNC_EPS) return true
  } else {
    if (Math.abs(num(stored.left) - measured.left) >= BOTTOM_PREFER_RESYNC_EPS) return true
  }
  if (Math.abs(num(stored.top) - measured.top) >= BOTTOM_PREFER_RESYNC_EPS) return true
  return false
}

/**
 * Writes top/left/bottom/right for a bottom-anchored field after sibling layout changes.
 * Keeps `preferBottom: true`, preserves `preferRight`. Does not touch dimensions (avoids feedback loops).
 */
export function applyBottomPreferPositionSync(element: IElement, geometry: FloatingLayoutGeometry, preferRight: boolean): void {
  if (!element.responsiveStyles) {
    element.responsiveStyles = { large: {} } as IElement['responsiveStyles']
  }
  if (!element.responsiveStyles.large) {
    element.responsiveStyles.large = {} as IElement['responsiveStyles']['large']
  }
  const large = element.responsiveStyles.large
  large.position = {
    top: geometry.top,
    left: geometry.left,
    bottom: geometry.bottom,
    right: geometry.right,
    preferBottom: true,
    preferRight,
  }
}

/** Default page margins when the document doesn't explicitly specify one — mirrors what every
 *  renderer falls back to (`PageRenderer.vue`, `PDFPage.vue`, `useContentAreaOffset.ts`).
 */
const DEFAULT_MARGIN = 48

/**
 * Compute the current content-area rect (width × height) for a page using the EXACT formula the
 * renderer uses for `.page-content`:
 *
 *   contentWidth  = pageWidth  - marginLeft  - marginRight
 *   contentHeight = pageHeight - (hasHeader ? headerHeight : marginTop)
 *                              - (hasFooter ? footerHeight : marginBottom)
 *
 * A custom header / footer REPLACES the corresponding page margin — so when the user grows the
 * header, the content area shrinks below by exactly that delta.
 */
export function computePageContentAreaSize(page: IPage | undefined | null): { width: number; height: number } | null {
  const opts = page?.component?.options as any
  if (!opts) return null
  const pageDims = opts.pageDimensions?.dimensions
  const margins = opts.pageDimensions?.margins
  if (!pageDims) return null
  const marginTop = margins?.top ?? DEFAULT_MARGIN
  const marginBottom = margins?.bottom ?? DEFAULT_MARGIN
  const marginLeft = margins?.left ?? DEFAULT_MARGIN
  const marginRight = margins?.right ?? DEFAULT_MARGIN
  const hasHeader = !!opts[PAGE_SECTIONS.HEADERS]
  const hasFooter = !!opts[PAGE_SECTIONS.FOOTERS]
  const headerH = hasHeader ? Number(opts.headerHeight ?? marginTop) : marginTop
  const footerH = hasFooter ? Number(opts.footerHeight ?? marginBottom) : marginBottom
  const width = Math.max(0, Number(pageDims.width) - marginLeft - marginRight)
  const height = Math.max(
    0,
    Number(pageDims.height) - (Number.isFinite(headerH) ? headerH : marginTop) - (Number.isFinite(footerH) ? footerH : marginBottom)
  )
  return { width, height }
}

/**
 * Walks page.children and, for every page-level floating (draggable fillable) element, recomputes
 * `position.top / bottom` so the element stays inside the CURRENT content area when the content
 * area shrinks or grows because the user edited header / footer height.
 *
 * Only VERTICAL axis is recomputed. Content WIDTH doesn't change when header / footer heights
 * change (only the page margins would affect width, and margin changes aren't routed through
 * this hook) — touching `left` / `right` on a stored position whose sum doesn't match any
 * historic container width would yank elements horizontally for no good reason.
 *
 * Section-aware anchor rules:
 *   • Only the HEADER side changed → only TOP-anchored elements get their stored position
 *     updated. The header's edge moved so top-anchored fields physically shift to keep the
 *     same distance from the header. Bottom-anchored fields are untouched — the footer
 *     didn't move, so their relationship to the bottom edge is already correct.
 *   • Only the FOOTER side changed → mirror image: only BOTTOM-anchored elements are
 *     recomputed; top-anchored fields are untouched.
 *   • BOTH sides changed (or neither specified) → recompute every floating element the same
 *     way it has always worked (keep the anchor edge, recompute the opposite edge).
 *
 * Anchor classification uses `preferBottom` when explicitly set, otherwise falls back to the
 * "user placed it closer to which edge?" heuristic (`bottom < top` → near the bottom).
 *
 * No-ops when no change to the derived content height is detected, or when an element lacks
 * `position` / `dimensions` data. Recurses into block descendants so fillables nested inside a
 * text block (page-level) are also handled. Does NOT touch header / footer section elements —
 * their coordinates are section-relative, not content-area-relative.
 *
 * @param page    The page whose children are rewritten in place.
 * @param options Optional hint telling the function which side's height actually changed.
 *                `{ headerChanged: true }` restricts recompute to top-anchored elements.
 *                `{ footerChanged: true }` restricts recompute to bottom-anchored elements.
 *                `{ headerChanged: true, footerChanged: true }` (or omitted) recomputes both.
 * @returns Whether any element's position was mutated.
 */
export function recomputePageLevelFloatingPositionsForContentResize(
  page: IPage | undefined | null,
  options?: { headerChanged?: boolean; footerChanged?: boolean }
): boolean {
  if (!page?.children?.length) return false

  // Hard gate: this helper only makes sense for v3+ documents, which store page-level
  // float positions content-relative (`top + bottom + height ≈ contentAreaHeight`).
  // v1 / v2 documents store positions page-relative (anchored to `.page-view`), so the
  // `top + bottom + height` invariant does NOT hold for them — running the recompute
  // would silently mutate authored positions away from where the user originally placed
  // them. On first mount this fires from `updatePageSectionHeights` for every doc
  // (because `.page-header` carries a 48px placeholder min-height that the measurement
  // reads as a "section height change from undefined"), so without this gate every v2
  // doc gets its page-level fillables silently rewritten on load.
  if (!usesContentRelativePagePositioning(page.version)) return false

  const contentSize = computePageContentAreaSize(page)
  if (!contentSize) return false
  const newHeight = contentSize.height
  if (!Number.isFinite(newHeight) || newHeight <= 0) return false

  // When the caller omits the hint (undefined), fall back to "both changed" so legacy call
  // sites keep working unchanged. When the caller explicitly passes the hint, respect it —
  // this is how we keep top-anchored fields out of the footer-resize pass and vice versa.
  const hintProvided = !!options && (options.headerChanged !== undefined || options.footerChanged !== undefined)
  const applyTopAnchor = hintProvided ? options?.headerChanged === true : true
  const applyBottomAnchor = hintProvided ? options?.footerChanged === true : true

  // If the hint tells us nothing actually changed on either side, there's nothing to do.
  if (!applyTopAnchor && !applyBottomAnchor) return false

  let mutated = false

  const visit = (nodes: IElement[] | undefined | null) => {
    if (!nodes?.length) return
    for (const el of nodes) {
      if (!el) continue

      const isFloating = el.component?.isDraggable && floatingElementsSet.has(el.type as never)
      if (isFloating) {
        const pos = el.responsiveStyles?.large?.position as any
        const dims = el.responsiveStyles?.large?.dimensions as any
        if (pos && dims && Number.isFinite(Number(dims.height))) {
          const top = Number(pos.top) || 0
          const bottom = Number(pos.bottom) || 0
          const height = Number(dims.height) || 0

          // Derive the content-area height that was in effect when this position was written.
          // If it still matches the current content area we have nothing to do.
          const oldHeight = top + bottom + height
          if (oldHeight > 0 && Math.abs(oldHeight - newHeight) > 0.5) {
            // `preferBottom` explicit > heuristic (element was placed closer to the bottom edge).
            const anchorBottom = pos.preferBottom === true || (pos.preferBottom !== false && bottom < top)
            if (anchorBottom) {
              // Bottom-anchored fields only care about FOOTER changes — skip on header-only resize.
              if (!applyBottomAnchor) {
                // Not applicable to this element for the requested side — leave it alone.
              } else {
                // Keep `bottom`, recompute `top` so the bottom edge distance stays fixed.
                pos.top = Math.max(0, newHeight - bottom - height)
                mutated = true
              }
            } else {
              // Top-anchored fields only care about HEADER changes — skip on footer-only resize.
              if (!applyTopAnchor) {
                // Not applicable to this element for the requested side — leave it alone.
              } else {
                // Keep `top`, recompute `bottom` so the top edge distance stays fixed.
                pos.bottom = Math.max(0, newHeight - top - height)
                mutated = true
              }
            }
          }
        }
      }

      // Recurse into block descendants — page-level fillables can live inside a text block.
      if (Array.isArray(el.children) && el.children.length) visit(el.children)
    }
  }

  visit(page.children)
  return mutated
}
